import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { NgbModal, NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { MasterInformationService } from '../../../Services/master-information.service';
import { ToastrService } from 'ngx-toastr';
import { apiEndPoints } from '../../../api/apiEndPoint';
import autoTable from 'jspdf-autotable';
import jsPDF from 'jspdf';
import * as XLSX from 'xlsx';

@Component({
  selector: 'app-percentage-configure',
  standalone: true,
  imports: [FormsModule, CommonModule, NgbModule, ReactiveFormsModule],
  templateUrl: './percentage-configure.component.html',
  styleUrl: './percentage-configure.component.css',
})
export class PercentageConfigureComponent implements OnInit {
  // ---------------------- Pagination ---------------------- //
  page = 1;
  pageSize = 5;

  // ---------------------- Other Declarations ---------------------- //
  isEditMode: boolean = false;
  percentageForm: FormGroup;
  cityList: any;
  changeCityId: any;
  selectedCityId: any;
  allCommissionList: any;
  cityId: any;

  constructor(
    private modalService: NgbModal,
    private masterService: MasterInformationService,
    private fb: FormBuilder,
    private toastr: ToastrService
  ) {
    this.percentageForm = this.fb.group({
      cityId: ['', [Validators.required]],
      containerRentalPercentage: [
        '',
        [Validators.required, Validators.min(0), Validators.max(100)],
      ],
      tripsPercentage: [
        '',
        [Validators.required, Validators.min(0), Validators.max(100)],
      ],
      disposalPercentage: [
        '',
        [Validators.required, Validators.min(0), Validators.max(100)],
      ],
    });
  }

  // ---------------------- OnInit ---------------------- //

  ngOnInit() {
    this.getCity();
    this.getCommissionList();
  }

  // ----------------------------------- Get All City List API ----------------------------------- //

  getCity() {
    this.masterService.getCityList(apiEndPoints.cityList).subscribe({
      next: (response: any) => {
        this.cityList = response.body.details;
      },
      error: (error) => {
        this.toastr.error(
          'An unexpected error occurred. Please try again later.',
          'Error',
          {
            timeOut: 3000,
            positionClass: 'toast-top-right',
            progressBar: true,
          }
        );
      },
    });
  }

  // ----------------------------------- City Change Dropdown ----------------------------------- //

  onFilterCityChange(event: Event) {
    const selectedCityId = (event.target as HTMLSelectElement).value;
    this.changeCityId = selectedCityId;
    if (selectedCityId == 'all') {
      this.getCommissionList();
      return;
    }
    this.getFilteredCommissionList();
  }

  onCityChange(event: Event) {
    const selectedCityId = (event.target as HTMLSelectElement).value;
    this.selectedCityId = selectedCityId;
    console.log(this.selectedCityId);
  }

  // ------------------------------- Open Modal ------------------------------- //

  openModal(content: any, isEditMode: boolean = false) {
    this.isEditMode = isEditMode;
    this.percentageForm.get('cityId')?.enable();
    this.percentageForm.reset();
    this.modalService.open(content, {
      centered: true,
      backdrop: 'static',
      size: 'md',
    });
  }

  // ------------------------------- Add Percentage Details ------------------------------- //

  addPercentageDetails(modal: any, type: any) {
    if (type == 'new') {
      if (this.percentageForm.valid) {
        const reqBody = {
          cityId: this.selectedCityId,
          containerRentalPercentage:
            this.percentageForm.value.containerRentalPercentage,
          tripsPercentage: this.percentageForm.value.tripsPercentage,
          disposalPercentage: this.percentageForm.value.disposalPercentage,
        };
        this.masterService
          .postAddCommission(apiEndPoints.addCommission, reqBody)
          .subscribe({
            next: (response: any) => {
              if (response.isSuccess == true) {
                console.log(response);
                modal.close();
                this.toastr.success(response.statusText, 'Success', {
                  timeOut: 3000,
                  positionClass: 'toast-top-right',
                  progressBar: true,
                });
                this.getCommissionList();
              }
            },
            error: (error) => {
              this.toastr.error(
                'An unexpected error occurred. Please try again later.',
                'Error',
                {
                  timeOut: 3000,
                  positionClass: 'toast-top-right',
                  progressBar: true,
                }
              );
            },
          });
      } else {
        this.percentageForm.markAllAsTouched();
      }
    } else {
      const reqBody = {
        cityId: this.cityId,
        containerRentalPercentage:
          this.percentageForm.value.containerRentalPercentage,
        tripsPercentage: this.percentageForm.value.tripsPercentage,
        disposalPercentage: this.percentageForm.value.disposalPercentage,
      };
      this.masterService
        .updateCommission(apiEndPoints.updateCommission, reqBody)
        .subscribe({
          next: (response: any) => {
            if (response.isSuccess == true) {
              console.log(response);
              modal.close();
              this.toastr.success(response.statusText, 'Success', {
                timeOut: 3000,
                positionClass: 'toast-top-right',
                progressBar: true,
              });
              this.getCommissionList();
            }
          },
          error: (error) => {
            this.toastr.error(
              'An unexpected error occurred. Please try again later.',
              'Error',
              {
                timeOut: 3000,
                positionClass: 'toast-top-right',
                progressBar: true,
              }
            );
          },
        });
    }
  }

  editItem(data: any, modal: any) {
    this.isEditMode = true;
    this.modalService.open(modal, {
      centered: true,
      backdrop: 'static',
      size: 'md',
    });

    this.cityId = data.cityId;

    this.percentageForm.patchValue({
      cityId: data.cityId,
      containerRentalPercentage: data.containerRentalPercentage,
      tripsPercentage: data.tripsPercentage,
      disposalPercentage: data.disposalPercentage,
    });

    this.percentageForm.get('cityId')?.disable();
  }

  // ------------------------------- Get Commission List API ------------------------------- //

  getCommissionList() {
    this.masterService
      .getCommissionList(apiEndPoints.commissionList)
      .subscribe({
        next: (response: any) => {
          if (response.isSuccess == true) {
            this.allCommissionList = response.body.details;
          }
        },
        error: (error) => {
          this.toastr.error(
            'An unexpected error occurred. Please try again later.',
            'Error',
            {
              timeOut: 3000,
              positionClass: 'toast-top-right',
              progressBar: true,
            }
          );
        },
      });
  }

  // ------------------------------- Get Filtered Commission List API ------------------------------- //

  getFilteredCommissionList() {
    this.masterService
      .filteredCommissionList(
        apiEndPoints.filteredCommissionList,
        this.changeCityId
      )
      .subscribe({
        next: (response: any) => {
          if (response.isSuccess == true) {
            this.allCommissionList = response.body.details;
          }
        },
        error: (error) => {
          this.toastr.error(
            'An unexpected error occurred. Please try again later.',
            'Error',
            {
              timeOut: 3000,
              positionClass: 'toast-top-right',
              progressBar: true,
            }
          );
        },
      });
  }

  // ----------------------------- Pagination ----------------------------- //
  get pagedData() {
    const start = (this.page - 1) * this.pageSize;
    const end = this.page * this.pageSize;
    return this.allCommissionList?.slice(start, end);
  }

  onPageChange(page: number): void {
    this.page = page;
  }

  // ----------------------------------- Clear Filter ----------------------------------- //

  clearFilter() {
    this.getCommissionList();
  }

  // ----------------------------------- Export to excel ----------------------------------- //

  exportToExcel() {
    const ws: XLSX.WorkSheet = XLSX.utils.json_to_sheet(this.allCommissionList);
    const wb: XLSX.WorkBook = { Sheets: { data: ws }, SheetNames: ['data'] };
    XLSX.writeFile(wb, 'PercentageConfig.xlsx');
  }

  // ------------------------------- Print List or Download PDF ------------------------------- //

  exportToPDF() {
    const doc = new jsPDF('landscape');
    const pageWidth = doc.internal.pageSize.width;
    const pdfFileName = 'PercentageConfig_List.pdf';

    const logo = new Image();
    logo.src = 'assets/Images/CDM-01.png';
    logo.onload = () => {
      const logoWidth = 28;
      const logoHeight = 20;
      const logoX = (pageWidth - logoWidth) / 2;
      const logoY = 10;
      doc.addImage(logo, 'PNG', logoX, logoY, logoWidth, logoHeight);

      const arabicAddressImg = new Image();
      arabicAddressImg.src = 'assets/Images/arabic address.jpeg';
      arabicAddressImg.onload = () => {
        const arabicImgWidth = 70;
        const arabicImgHeight = 20;
        const arabicImgX = pageWidth - arabicImgWidth - 10;
        const arabicImgY = 10;
        doc.addImage(
          arabicAddressImg,
          'JPEG',
          arabicImgX,
          arabicImgY,
          arabicImgWidth,
          arabicImgHeight
        );

        // Add website below Arabic address image
        doc.setFontSize(10);
        doc.setTextColor(0, 0, 255);
        doc.text(
          'www.smart­sort.net',
          arabicImgX + arabicImgWidth / 2,
          arabicImgY + arabicImgHeight + 5,
          { align: 'left' }
        );

        const englishAddressImg = new Image();
        englishAddressImg.src = 'assets/Images/english address.jpeg';
        englishAddressImg.onload = () => {
          const englishImgWidth = 70;
          const englishImgHeight = 20;
          const englishImgX = 10;
          const englishImgY = 10;
          doc.addImage(
            englishAddressImg,
            'JPEG',
            englishImgX,
            englishImgY,
            englishImgWidth,
            englishImgHeight
          );

          // Add website below English address image
          doc.setFontSize(10);
          doc.setTextColor(0, 0, 255);
          doc.text(
            'www.smart­sort.net',
            englishImgX + englishImgWidth / 11,
            englishImgY + englishImgHeight + 5,
            { align: 'left' }
          );

          // Reset text color to black (optional, in case you add more text later)
          doc.setTextColor(0, 0, 0);

          doc.setFont('helvetica', 'bold');
          doc.setFontSize(14);
          const title = 'Percentage Configuration List';
          const titleY = logoY + logoHeight + 10;
          doc.text(title, pageWidth / 2, titleY, { align: 'center' });

          // Table headers
          const headers = [
            [
              { content: 'City' },
              { content: 'Container Rental Percentage	' },
              { content: 'Trip Charges Percentage' },
              { content: 'Disposal Charges Percentage' },
            ],
          ];

          let tableBody: any[] = [];
          // Ensure Year is filled for every row
          this.pagedData.forEach((data: any) => {
            tableBody.push([
              data.cityName,
              data.containerRentalPercentage,
              data.tripsPercentage,
              data.disposalPercentage,
            ]);
          });

          // Generate table
          autoTable(doc, {
            startY: titleY + 10,
            head: headers,
            body: tableBody,
            styles: {
              fontSize: 8,
              cellPadding: 2,
              lineWidth: 0.3,
              lineColor: [50, 50, 50],
              halign: 'center',
            },
            theme: 'grid',
            headStyles: {
              fillColor: [176, 220, 101],
              textColor: [0, 0, 0],
              fontStyle: 'bold',
              lineColor: [50, 50, 50],
              lineWidth: 0.3,
            },
            alternateRowStyles: { fillColor: [245, 245, 245] },
            columnStyles: { 0: { halign: 'center', fontStyle: 'bold' } },
            margin: { top: titleY + 20 },
            pageBreak: 'auto',
            rowPageBreak: 'avoid',
            didDrawPage: function (data) {
              const pageCount = doc.getNumberOfPages();
              doc.setFontSize(10);
              doc.text(
                `Page ${pageCount}`,
                pageWidth - 20,
                doc.internal.pageSize.height - 10
              );
            },
          });

          // Save the PDF
          doc.save(pdfFileName);
        };
      };
    };
  }
}
